import { useEffect, useRef } from "react";

// Hard 24h server-side cutoff is a security guarantee we do not soften. This
// hook exists to pick the *timing* of re-authentication so users are rarely
// interrupted mid-work: prefer logging out idle users a bit early, and warn
// active users before the hard cutoff hits.
const TICK_MS = 60 * 1000;
const PROACTIVE_LOGOUT_REMAINING_MS = 12 * 60 * 60 * 1000;
const PROACTIVE_LOGOUT_IDLE_MS = 30 * 60 * 1000;
const WARNING_REMAINING_MS = 60 * 60 * 1000;
const WARNING_TOAST_INTERVAL_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = [
	"mousedown",
	"keydown",
	"touchstart",
	"scroll",
] as const;

/**
 * Where tabs publish the last time this *user* did anything.
 *
 * Idleness is a property of the person, not of a browser tab. Tracking it per
 * tab meant a background tab went idle on its own schedule, called
 * `onIdleLogout`, cleared the token — and the cross-tab `storage` listener in
 * `auth.tsx` then mirrored that logout into the tab the user was actively
 * typing in. With two or three tabs open, which is how this product is used,
 * that was close to guaranteed.
 */
const LAST_ACTIVITY_KEY = "oneai:lastActivityAt";

/**
 * Throttle for the shared write. Activity events fire continuously while
 * scrolling; the reader only looks once per {@link TICK_MS}, so a write per
 * event would buy nothing and put localStorage in the scroll path.
 */
const ACTIVITY_WRITE_INTERVAL_MS = 30 * 1000;

/**
 * The last activity any tab published, or 0 when there is none to read.
 *
 * Storage is best-effort by construction — it throws in Safari's private mode
 * and can hold anything a previous version wrote — and the fallback is simply
 * this tab's own timestamp, which is the behaviour that existed before. So a
 * failure here degrades to the old semantics rather than to no timeout at all.
 */
const readSharedActivity = (): number => {
	try {
		const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
		if (raw === null) {
			return 0;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : 0;
	} catch {
		return 0;
	}
};

export function useSessionExpiryMonitor(options: {
	enabled: boolean;
	sessionExpiresAt: string | null | undefined;
	onIdleLogout: () => void;
	/** Called with the ms remaining until the hard cutoff so the warning can show
	 * a concrete countdown ("ends in N min"). */
	onWarn: (remainingMs: number) => void;
	/**
	 * Fired the moment the session actually reaches its hard cutoff, so the UI
	 * flips to logged-out immediately instead of waiting for the next request to
	 * 401. Safe to make idempotent-heavy: it is expected to dedupe with the
	 * reactive 401 path (both funnel through the debounced `handleSessionExpired`).
	 */
	onExpired: () => void;
}): void {
	const { enabled, sessionExpiresAt, onIdleLogout, onWarn, onExpired } =
		options;

	const lastActivityAtRef = useRef<number>(Date.now());
	const lastActivityWriteAtRef = useRef<number>(0);
	const lastWarningAtRef = useRef<number>(0);
	const onIdleLogoutRef = useRef(onIdleLogout);
	const onWarnRef = useRef(onWarn);
	const onExpiredRef = useRef(onExpired);

	onIdleLogoutRef.current = onIdleLogout;
	onWarnRef.current = onWarn;
	onExpiredRef.current = onExpired;

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const touch = () => {
			const now = Date.now();
			lastActivityAtRef.current = now;
			// Throttled: see ACTIVITY_WRITE_INTERVAL_MS. The staleness this admits
			// is bounded by the interval and always errs towards *more* idle, which
			// the tab's own ref corrects for locally.
			if (now - lastActivityWriteAtRef.current < ACTIVITY_WRITE_INTERVAL_MS) {
				return;
			}
			lastActivityWriteAtRef.current = now;
			try {
				window.localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
			} catch {
				// Storage unavailable (private mode, quota). This tab keeps its own
				// timestamp and behaves exactly as it did before the shared key
				// existed; nothing here is worth interrupting the user over.
			}
		};
		// Deliberately NOT called once on mount. A tab's own `lastActivityAtRef`
		// already starts at mount time, which is the pre-existing behaviour; only
		// real interaction is worth publishing to the other tabs, so that opening
		// a window — or a browser restoring three of them — does not read as the
		// user being present.
		for (const ev of ACTIVITY_EVENTS) {
			window.addEventListener(ev, touch, { passive: true });
		}
		return () => {
			for (const ev of ACTIVITY_EVENTS) {
				window.removeEventListener(ev, touch);
			}
		};
	}, [enabled]);

	useEffect(() => {
		if (!enabled || !sessionExpiresAt) {
			return;
		}
		const expiresAtMs = new Date(sessionExpiresAt).getTime();
		if (!Number.isFinite(expiresAtMs)) {
			return;
		}

		const tick = () => {
			const now = Date.now();
			const remaining = expiresAtMs - now;
			if (remaining <= 0) {
				// Session has actually ended — flip the UI now rather than waiting
				// for the user's next request to 401. Debounced downstream, so a
				// near-simultaneous reactive 401 does not double-toast/redirect.
				onExpiredRef.current();
				return;
			}
			// The most recent activity ANY tab saw. Taking the max rather than the
			// shared value alone keeps this tab's own, unthrottled timestamp
			// authoritative for itself.
			const idle =
				now - Math.max(lastActivityAtRef.current, readSharedActivity());

			if (
				remaining < PROACTIVE_LOGOUT_REMAINING_MS &&
				idle > PROACTIVE_LOGOUT_IDLE_MS
			) {
				onIdleLogoutRef.current();
				return;
			}

			if (
				remaining < WARNING_REMAINING_MS &&
				now - lastWarningAtRef.current > WARNING_TOAST_INTERVAL_MS
			) {
				lastWarningAtRef.current = now;
				onWarnRef.current(remaining);
			}
		};

		tick();
		const interval = window.setInterval(tick, TICK_MS);
		// The 60s interval handles idle-logout + warnings; schedule a dedicated
		// one-shot at the exact cutoff so the hard-expiry flip is immediate rather
		// than up to TICK_MS late. `tick()` above already covers an already-past
		// expiry, so only arm this when the cutoff is still in the future.
		const untilExpiry = expiresAtMs - Date.now();
		const expiryTimeout =
			untilExpiry > 0
				? window.setTimeout(() => onExpiredRef.current(), untilExpiry)
				: undefined;
		return () => {
			window.clearInterval(interval);
			if (expiryTimeout !== undefined) {
				window.clearTimeout(expiryTimeout);
			}
		};
	}, [enabled, sessionExpiresAt]);
}
